// ===== POC Cuidados Paliativos (PNCP) - plano de cuidados, óbito e local de cuidado =====
// Substitui o antigo mocks/carePlanStorage.js. As constantes e o formato dos
// objetos são os mesmos de antes; as funções agora chamam a API.

import { apiGet, apiPost, apiPut } from "./http";

// Evento do eventBus para a faixa do cabeçalho atualizar quando o plano é salvo.
export const CARE_PLAN_UPDATED_EVENT = "carePlanUpdated";

// PPS (Palliative Performance Scale): funcionalidade de 0% a 100%, de 10 em 10.
export const PPS_OPTIONS = Array.from({ length: 11 }, (_, i) => {
  const value = 100 - i * 10;
  return { _id: String(value), name: `${value}%` };
});

export const PLACE_OPTIONS = [
  { _id: "casa", name: "Em casa" },
  { _id: "hospital", name: "No hospital" },
  { _id: "indefinido", name: "Ainda não definido" }
];

// Medidas de suporte: o que o paciente (ou a família) aceita ou não.
export const SUPPORT_MEASURES = [
  { _id: "rcp", name: "Reanimação cardiopulmonar" },
  { _id: "iot", name: "Intubação e ventilação mecânica" },
  { _id: "uti", name: "Transferência para UTI" },
  { _id: "sonda", name: "Alimentação por sonda" },
  { _id: "dialise", name: "Hemodiálise" },
  { _id: "antibiotico", name: "Antibióticos" }
];

export const SUPPORT_DECISIONS = [
  { _id: "sim", name: "Aceita" },
  { _id: "nao", name: "Não aceita" },
  { _id: "discutir", name: "A discutir" }
];

// Quem participou da conversa sobre as diretivas antecipadas.
export const DIRECTIVE_PARTICIPANTS = [
  { _id: "paciente", name: "Paciente" },
  { _id: "familia", name: "Familiares" },
  { _id: "cuidadora", name: "Cuidadora" },
  { _id: "equipe", name: "Outros profissionais" }
];

export const DEATH_PLACE_OPTIONS = [
  { _id: "casa", name: "Em casa" },
  { _id: "hospital", name: "No hospital" },
  { _id: "outro", name: "Outro local" }
];

export const DESIRED_PLACE_OPTIONS = [
  { _id: "sim", name: "Sim" },
  { _id: "nao", name: "Não" },
  { _id: "nao_sabe", name: "Não se sabe" }
];

// Local de cuidado atual. São as mesmas 3 etapas (fixas) do quadro do Ambulatório.
export const CARE_LOCATION = {
  ambulatorio: "ambulatorio",
  casa: "casa",
  internacao: "internacao"
};

// Registro das diretivas antecipadas (Resolução CFM 1.995/2012: o médico
// registra no prontuário; cartório é opcional).
export function emptyDirective() {
  return {
    doctorName: "",
    participants: [],
    patientCouldDecide: null,
    decidedBy: "",
    proxyName: "",
    proxyRelation: "",
    wishes: "",
    versions: []
  };
}

export function emptyCarePlan() {
  return {
    pps: "",
    goals: "",
    carePlace: "",
    deathPlace: "",
    supportMeasures: {},
    directiveRegistered: false,
    directiveDate: "",
    directive: emptyDirective(),
    caregiverName: "",
    caregiverRelation: "",
    updatedAt: null,
    updatedBy: ""
  };
}

export function placeLabel(id) {
  const option = PLACE_OPTIONS.find((o) => o._id === id);
  return option ? option.name : "";
}

// Mensagem de boas-vindas padrão. O link do app vai por SMS; esta mensagem
// aparece para o paciente quando ele abre o app (já baixado).
export function defaultWelcomeMessage(patientName) {
  const firstName = (patientName || "").trim().split(" ")[0] || "";
  const greeting = firstName ? `Olá, ${firstName}!` : "Olá!";
  return `${greeting} Aqui é a equipe de cuidados paliativos. A partir de hoje, vamos acompanhar você em casa pelo aplicativo Lila. Por ele, você conta como está se sentindo e fala com a equipe sempre que precisar.`;
}

// Datas só de dia: as telas usam DD/MM/YYYY; a API, YYYY-MM-DD.
function toApiDay(day) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(day || "");
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return /^\d{4}-\d{2}-\d{2}/.test(day || "") ? day.slice(0, 10) : null;
}

function fromApiDay(day) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day || "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
}

// ===== Plano de cuidados (Tela 4) =====

function toCarePlan(r) {
  const directive = { ...emptyDirective(), ...(r.Directive || {}) };
  // A API devolve as versões da mais nova para a mais antiga; a tela usa a ordem inversa.
  directive.versions = [...(r.DirectiveVersions || [])].reverse().map((v) => ({
    id: v.Id,
    savedAt: v.SavedAt,
    by: v.ProfessionalName,
    fingerprint: v.Fingerprint
  }));
  return {
    pps: r.Pps == null ? "" : String(r.Pps),
    goals: r.Goals || "",
    carePlace: r.CarePlace || "",
    deathPlace: r.DeathPlace || "",
    supportMeasures: r.SupportMeasures || {},
    directiveRegistered: Boolean(r.DirectiveRegistered),
    directiveDate: fromApiDay(r.DirectiveDate),
    directive,
    caregiverName: r.CaregiverName || "",
    caregiverRelation: r.CaregiverRelation || "",
    updatedAt: r.UpdatedAt,
    updatedBy: r.UpdatedByName || ""
  };
}

/** Plano do paciente, ou null se ainda não existe. */
export async function getCarePlan(patientId) {
  const data = await apiGet(`/patients/${patientId}/care-plan`);
  return data && data.Exists ? toCarePlan(data) : null;
}

/** Salva o plano. A API versiona a diretiva e gera a produção do planejamento antecipado. */
export async function saveCarePlan(patientId, plan) {
  // eslint-disable-next-line no-unused-vars
  const { versions, ...directive } = plan.directive || {};
  const data = await apiPut(`/patients/${patientId}/care-plan`, {
    Pps: plan.pps === "" || plan.pps == null ? null : Number(plan.pps),
    Goals: plan.goals,
    CarePlace: plan.carePlace,
    DeathPlace: plan.deathPlace,
    SupportMeasures: plan.supportMeasures,
    DirectiveRegistered: Boolean(plan.directiveRegistered),
    DirectiveDate: toApiDay(plan.directiveDate),
    Directive: directive,
    CaregiverName: plan.caregiverName,
    CaregiverRelation: plan.caregiverRelation
  });
  return toCarePlan(data);
}

// ===== Óbito e luto (Tela 6) =====

function toDeathRecord(r) {
  return {
    deathDate: fromApiDay(r.DeathDate),
    deathPlace: r.DeathPlace || "",
    wasDesired: r.WasDesired || "",
    griefTrail: Boolean(r.GriefTrail),
    griefActionIds: r.GriefActionIds || [],
    patientIsDeceased: Boolean(r.PatientIsDeceased),
    registeredAt: r.RegisteredAt,
    registeredBy: r.RegisteredByName || "",
    updatedAt: r.UpdatedAt
  };
}

/** Registro de óbito do paciente, ou null se não houver (a API responde 204). */
export async function getDeathRecord(patientId) {
  const data = await apiGet(`/patients/${patientId}/death-record`);
  return data && data.DeathPlace ? toDeathRecord(data) : null;
}

/**
 * Registra o óbito. A API marca o paciente como falecido (LifeStatus 3 e data do óbito)
 * e, com a trilha de luto, cria as ações D+7 e D+30.
 */
export async function saveDeathRecord(patientId, record) {
  const data = await apiPut(`/patients/${patientId}/death-record`, {
    DeathDate: toApiDay(record.deathDate),
    DeathPlace: record.deathPlace,
    WasDesired: record.wasDesired || null,
    GriefTrail: Boolean(record.griefTrail)
  });
  return toDeathRecord(data);
}

// ===== Local de cuidado: ambulatório, em casa, internação =====

// { location, atHome, history: [{ type: "casa" | "readmissao" | "internacao", at, by, message, smsSent }] }
// O histórico fica do mais antigo para o mais novo.
function toHomeRecord(r) {
  const location = r.Location || CARE_LOCATION.ambulatorio;
  return {
    location,
    atHome: location === CARE_LOCATION.casa,
    history: [...(r.History || [])].reverse().map((e) => ({
      id: e.Id,
      type: e.Type,
      at: e.CreatedAt,
      by: e.ProfessionalName || "",
      message: e.Message || "",
      smsSent: Boolean(e.SmsSent)
    }))
  };
}

export async function getHomeRecord(patientId) {
  return toHomeRecord(await apiGet(`/patients/${patientId}/home-care`));
}

// Envia para casa. Com Palliative:SendSms ligado, a API manda a mensagem e o link do app por SMS.
export async function sendPatientHome(patientId, message) {
  return toHomeRecord(await apiPost(`/patients/${patientId}/home-care/send`, { Message: message }));
}

// Volta para o ambulatório (vindo de casa ou da internação).
export async function readmitPatient(patientId) {
  return toHomeRecord(await apiPost(`/patients/${patientId}/home-care/readmit`));
}

export async function hospitalizePatient(patientId) {
  return toHomeRecord(await apiPost(`/patients/${patientId}/home-care/hospitalize`));
}

// ===== Quadro do Ambulatório =====
// 3 etapas fixas (ambulatorio, casa, internacao), montadas a partir do local de cuidado.
export async function getAmbulatoryBoard() {
  const data = await apiGet("/ambulatory-board");
  return (data.Columns || []).map((column) => ({
    kind: column.Kind,
    title: column.Title,
    cards: (column.Cards || []).map((card) => ({
      patientId: card.PatientId,
      name: card.Name,
      photo: card.Photo,
      gender: card.Gender,
      age: card.Age,
      pps: card.Pps,
      summary: card.Summary || "",
      locationSince: card.LocationSince
    }))
  }));
}
